import type { LocationResponse } from "@somewear/api";
import { center as turfCenter, distance as turfDistance } from "@turf/turf";
import type { Feature, Point } from "geojson";
import type { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb";

const CLEAN_MIN_DISTANCE_IN_METERS = 50;

export interface ProcessedLocation extends LocationResponse.AsObject {
	exitTimestamp?: Timestamp.AsObject;
	exploded?: boolean;
	virtual?: boolean;
}

export interface VirtualLocation extends ProcessedLocation {
	exploded: boolean;
	virtual: true;
}

export function isVirtual(location: ProcessedLocation): location is VirtualLocation {
	return location.virtual === true && location.exploded !== undefined;
}

// this type is used locally for breadcrumb clustering purposes, it should not leak into the greater codebase at this time
type LocationNode = ProcessedLocation & {
	next: LocationNode | undefined;
	prev?: LocationNode | undefined;
	collapsedLocations: LocationNode[];
	added?: boolean;
};

/**
 * Builds a linked list from tail to head for a given set of locations (assumes sorted by timestamp descending)
 * and returns all the nodes sorted from head to tail (where the head point is the most recent location).
 *
 * note: the ergonomics of this function would be cleaner if the linked list also went from head -> tail.
 */
export function getNodeList(locations: LocationResponse.AsObject[]): LocationNode[] {
	let prevNode: LocationNode | undefined;
	return locations.map((location) => {
		const node = {
			...location,
			next: prevNode,
			collapsedLocations: [],
		} as LocationNode;

		prevNode = node;
		return node;
	});
}

/**
 * Used to find the center point of a given location node.
 * If the node isn't virtual, it acts as a simple transform from location -> point.
 * If the node is virtual, it finds the center point of the collapse locations.
 *
 * This is helpful when trying to measure the distance between a virtual node and a real node,
 * to determine whether the real node should be collapsed into the virtual node.
 *
 * We use the center point of a set of clustered breadcrumbs for distance measurements as it is a
 * more accurate representation of the asset's actual location over any single real node.
 */
function getBreadcrumbClusterCenterPoint(node: LocationNode): Point {
	if (!isVirtual(node)) {
		return {
			type: "Point",
			coordinates: [node.coordinate!.longitude, node.coordinate!.latitude],
		};
	}

	const pointFeatures = node.collapsedLocations
		.mapNotNull((location) => location.coordinate)
		.map((coord) => {
			return {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: [coord.longitude, coord.latitude],
				},
			} as Feature<Point>;
		});

	const centerFeature = turfCenter({
		type: "FeatureCollection",
		features: pointFeatures,
	});

	return centerFeature.geometry as Point;
}

/**
 * This function handles simplifying a track of locations (breadcrumb trail) into a smaller set of locations.
 * It does this by collapsing breadcrumbs that are close together into a single "virtual" node.
 *
 * If a virtual node is selected, it will be exploded into its constituent nodes.
 *
 * note: the ergonomics of this function would be better if the root of the linked list was the head rather than the tail
 */
export function collapseNodes(
	head: LocationNode,
	tail: LocationNode,
	selectedLocationId: string | undefined,
): ProcessedLocation[] {
	if (head.id === tail.id || head.next === undefined) return [head];
	if (head.next.id === tail.id) return [head, tail];

	// start at the second node, the first is not collapsible
	let anchorNode = head.next;
	let node = anchorNode.next;

	// a location with no coordinate (an SOS emitted without a fix) can't anchor a cluster
	while (anchorNode.coordinate === undefined || node === undefined) {
		if (node?.next === undefined) return [head, tail];
		anchorNode = node;
		node = node.next;
	}
	const cleanedNodeList: LocationNode[] = [];
	let hasExplodedLocation = false;

	do {
		const centerCoord = getBreadcrumbClusterCenterPoint(anchorNode).coordinates;
		const currentCoord = node.coordinate;

		if (currentCoord === undefined) {
			if (node.next?.next === undefined) break;
			node = node.next;
			continue;
		}

		const distance = turfDistance(
			[centerCoord[0], centerCoord[1]],
			[currentCoord.longitude, currentCoord.latitude],
			{
				units: "meters",
			},
		);

		if (distance < CLEAN_MIN_DISTANCE_IN_METERS) {
			const previousCollapsedLocations = anchorNode.virtual
				? anchorNode.collapsedLocations
				: [anchorNode];

			const collapsedLocations = [...previousCollapsedLocations, node];

			const isExploded =
				selectedLocationId !== undefined &&
				collapsedLocations.find((it) => it.id === selectedLocationId) !== undefined;

			if (isExploded) hasExplodedLocation = true;

			const virtualNode = {
				...anchorNode,
				coordinate: {
					longitude: centerCoord[0],
					latitude: centerCoord[1],
					longitudeDeprecated: 0,
					latitudeDeprecated: 0,
				},
				next: node.next,
				collapsedLocations: collapsedLocations,
				prev: anchorNode.prev,
				exploded: isExploded,
				virtual: true,
			} as LocationNode;

			// we have to update its parent's pointer to the virtual node
			if (anchorNode.prev !== undefined) anchorNode.prev.next = virtualNode;
			anchorNode = virtualNode;
		} else {
			anchorNode.added = true;
			cleanedNodeList.push(anchorNode);
			node.prev = anchorNode;
			anchorNode = node;
		}

		// skip the head node, as it's not collapsible
		if (node.next?.next === undefined) break;
		node = node.next;
	} while (true);

	// make sure the last node gets added
	if (!anchorNode.added) cleanedNodeList.push(anchorNode);

	// explodes the locations for the selectedNode
	if (hasExplodedLocation) {
		const explodedLocation = cleanedNodeList.find((it) => isVirtual(it) && it.exploded)!;
		const explodedLocationIndex = cleanedNodeList.indexOf(explodedLocation);
		cleanedNodeList.splice(
			explodedLocationIndex,
			1, // todo: this deletes the virtual location (we might want this later)
			...explodedLocation.collapsedLocations,
		);
	}

	return [head, ...cleanedNodeList, tail] as ProcessedLocation[];
}
